import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Container,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash, IconMail } from "@tabler/icons-react";
import { Redirect } from "wouter";
import { useStore } from "../store";
import { jsonGet, jsonPost, jsonPut, jsonDelete } from "../api";

const ROLE_OPTIONS = [
  { value: "TenantAdmin", label: "Admin" },
  { value: "TenantUser", label: "Member" },
];

const ROLE_LABELS = {
  TenantOwner: "Owner",
  TenantAdmin: "Admin",
  TenantUser: "Member",
  GlobalAdmin: "Global Admin",
};

export default function Team() {
  const authToken = useStore((state) => state.authToken);
  const [members, setMembers] = useState(null);
  const [inviteOpened, { open: openInvite, close: closeInvite }] = useDisclosure(false);
  const [inviteForm, setInviteForm] = useState({ email: "", firstName: "", lastName: "", role: "TenantUser" });
  const [inviting, setInviting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const loadMembers = () => {
    jsonGet("team")
      .then((r) => setMembers(r?.items || []))
      .catch(() => setMembers([]));
  };

  useEffect(() => {
    if (!authToken) return;
    loadMembers();
  }, [authToken]);

  const handleInvite = async () => {
    if (!inviteForm.email) return;
    setInviting(true);
    try {
      await jsonPost("team", inviteForm);
      notifications.show({ color: "green", message: "Invitation sent!" });
      closeInvite();
      setInviteForm({ email: "", firstName: "", lastName: "", role: "TenantUser" });
      loadMembers();
    } catch {
      // error already shown by api.js
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await jsonPut(`team/${userId}/role`, { role });
      loadMembers();
    } catch {
      // error shown by api.js
    }
  };

  const handleRemove = async (userId) => {
    try {
      await jsonDelete(`team/${userId}`);
      notifications.show({ color: "green", message: "Member removed." });
      setConfirmRemove(null);
      loadMembers();
    } catch {
      // error shown by api.js
    }
  };

  const handleResend = async (userId) => {
    try {
      await jsonPost(`team/${userId}/resend`);
      notifications.show({ color: "green", message: "Invitation resent!" });
    } catch {
      // error shown by api.js
    }
  };

  if (!authToken) return <Redirect to="/auth/login" />;

  return (
    <Container size="md" mt="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Team</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openInvite}>
          Invite Member
        </Button>
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Joined</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {members?.map((m) => (
            <Table.Tr key={m.id}>
              <Table.Td>
                {[m.firstName, m.lastName].filter(Boolean).join(" ") || "—"}
              </Table.Td>
              <Table.Td>{m.email}</Table.Td>
              <Table.Td>
                {m.role === "TenantOwner" ? (
                  <Badge variant="light" color="blue">Owner</Badge>
                ) : (
                  <Select
                    data={ROLE_OPTIONS}
                    value={m.role}
                    onChange={(val) => val && handleRoleChange(m.id, val)}
                    size="xs"
                    w={120}
                    allowDeselect={false}
                  />
                )}
              </Table.Td>
              <Table.Td>
                <Badge
                  color={m.isActive ? "green" : "yellow"}
                  variant="light"
                  size="sm"
                >
                  {m.isActive ? "Active" : "Invited"}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  {new Date(m.created).toLocaleDateString()}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  {!m.isActive && (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="blue"
                      onClick={() => handleResend(m.id)}
                      leftSection={<IconMail size={14} />}
                    >
                      Resend
                    </Button>
                  )}
                  {m.role !== "TenantOwner" && (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      onClick={() => setConfirmRemove(m)}
                      leftSection={<IconTrash size={14} />}
                    >
                      Remove
                    </Button>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {/* Invite Modal */}
      <Modal opened={inviteOpened} onClose={closeInvite} title="Invite Team Member">
        <Stack>
          <TextInput
            label="Email"
            required
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
          />
          <Group grow>
            <TextInput
              label="First Name"
              value={inviteForm.firstName}
              onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
            />
            <TextInput
              label="Last Name"
              value={inviteForm.lastName}
              onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
            />
          </Group>
          <Select
            label="Role"
            data={ROLE_OPTIONS}
            value={inviteForm.role}
            onChange={(val) => setInviteForm({ ...inviteForm, role: val })}
            allowDeselect={false}
          />
          <Button onClick={handleInvite} loading={inviting}>
            Send Invitation
          </Button>
        </Stack>
      </Modal>

      {/* Confirm Remove Modal */}
      <Modal
        opened={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        title="Remove Team Member"
        size="sm"
      >
        <Stack>
          <Text>
            Are you sure you want to remove{" "}
            <strong>{confirmRemove?.email}</strong> from the team?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmRemove(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => handleRemove(confirmRemove.id)}>
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
